import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { DaySchedule, LiveClassScheduleItem } from '../types'
import { SessionCard } from './SessionCard'
import { EmptySlot } from './EmptySlot'

interface DayColumnProps {
  day: DaySchedule
}

// Helper functions for live/upcoming detection
let isLive = (item: LiveClassScheduleItem): boolean => {
  let now = new Date()
  let startTime = new Date(`${item.sessionTime}Z`)
  let endTime = item.endTime
    ? new Date(`${item.endTime}Z`)
    : new Date(startTime.getTime() + 2 * 60 * 60 * 1000) // Default 2hr
  return now >= startTime && now <= endTime
}

let isUpcoming = (item: LiveClassScheduleItem): boolean => {
  let now = new Date()
  let startTime = new Date(`${item.sessionTime}Z`)
  let timeDiff = startTime.getTime() - now.getTime()
  return timeDiff > 0 && timeDiff <= 60 * 60 * 1000 // Within 1 hour
}

export let DayColumn = ({ day }: DayColumnProps) => {
  let dayDate = dayjs(day.date)
  let isToday = dayDate.isSame(dayjs(), 'day')

  return (
    <Box
      component="article"
      aria-label={`${day.dayName} ${dayDate.format('MMMM D, YYYY')} classes`}
    >
      {/* Day Header */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          variant="caption"
          component="span"
          sx={{
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: isToday ? 'primary.main' : 'text.secondary',
            mb: 0.5,
          }}
        >
          {day.dayName}
        </Typography>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontWeight: 700,
            color: isToday ? 'primary.main' : 'text.primary',
          }}
        >
          <span className="sr-only">{dayDate.format('MMMM D, YYYY')}</span>
          <span aria-hidden="true">{dayDate.format('D')}</span>
        </Typography>
      </Box>

      {/* Sessions */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        role="list"
        aria-label={`${day.dayName} class sessions`}
      >
        {/* Morning Session */}
        <Box role="listitem">
          {day.morningClass ? (
            <SessionCard
              session={day.morningClass}
              isLive={isLive(day.morningClass)}
              isUpcoming={isUpcoming(day.morningClass)}
              timeSlot="morning"
            />
          ) : (
            <EmptySlot message="No AM Class" />
          )}
        </Box>

        {/* Evening Session */}
        <Box role="listitem">
          {day.eveningClass ? (
            <SessionCard
              session={day.eveningClass}
              isLive={isLive(day.eveningClass)}
              isUpcoming={isUpcoming(day.eveningClass)}
              timeSlot="evening"
            />
          ) : (
            <EmptySlot message="No PM Class" />
          )}
        </Box>
      </Box>
    </Box>
  )
}
